import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { CommonService } from '../services/common.service';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    baseUrl: string;

    constructor(
        private http: HttpClient,
        public commonService: CommonService
    ) {
        this.baseUrl = environment.API_URL;
    }

    themeSettings(code: string) {
        return this.http.get<any>(this.baseUrl + "api/company/get-company-detail?companyCode=" + code)
            .pipe(map(response => {
                return response;
            }));
    }

    loadTheme(code: string) {
        this.themeSettings(code).subscribe({
            next: (res) => {
                const result = res['result']['companyColorTheme'] ? JSON.parse(res['result']['companyColorTheme']) : null;
                this.setCSSVariables({
                    "primary-color": result ? result['primaryColor'] : '#005372',
                    "secondary-color": result ? result['secondaryColor'] : '#147498'
                });
                if (res['result']['companyLogoURL']) {
                    this.commonService.setCompanyLogo(res['result']['companyLogoURL'] || "./assets/img/bertelsmann.png");
                } else {
                    this.commonService.setCompanyLogo("./assets/img/bertelsmann.png");
                }
                
                if (res?.result?.isOktaLoginEnabled) {
                    this.commonService.setOktaLink(res?.result['oktaAppEmbedLoginLink']);
                }
            },
            error: () => {
                this.setCSSVariables({
                    "primary-color": "#005372",
                    "secondary-color": "#147498"
                });
                this.commonService.setCompanyLogo("./assets/img/bertelsmann.png");
            }
        })
    }

    private setCSSVariables(theme: any) {
        const root = document.documentElement;
        root.style.setProperty('--primary-color', theme['primary-color']);
        root.style.setProperty('--secondary-color', theme['secondary-color']);
        // WCAG 1.4.3/1.4.11: pick whichever of black/white gives higher contrast
        // against the tenant's secondary color, so button text stays readable
        // (including for color-blind users) no matter what color the API returns.
        root.style.setProperty('--btn-text-color', this.getAccessibleTextColor(theme['secondary-color']));
    }

    /**
     * Returns '#000000' or '#ffffff' — whichever yields the higher WCAG
     * contrast ratio against the given background color.
     */
    private getAccessibleTextColor(hexColor: string): string {
        const rgb = this.hexToRgb(hexColor);
        if (!rgb) {
            return '#ffffff';
        }
        const bgLuminance = this.relativeLuminance(rgb);
        const contrastWithBlack = (bgLuminance + 0.05) / (0 + 0.05);
        const contrastWithWhite = (1 + 0.05) / (bgLuminance + 0.05);
        return contrastWithBlack >= contrastWithWhite ? '#000000' : '#ffffff';
    }

    private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
        if (!hex) {
            return null;
        }
        let normalized = hex.replace('#', '').trim();
        if (normalized.length === 3) {
            normalized = normalized.split('').map(c => c + c).join('');
        }
        if (normalized.length !== 6) {
            return null;
        }
        const num = parseInt(normalized, 16);
        if (isNaN(num)) {
            return null;
        }
        return {
            r: (num >> 16) & 255,
            g: (num >> 8) & 255,
            b: num & 255
        };
    }

    private relativeLuminance(rgb: { r: number; g: number; b: number }): number {
        const channel = (c: number) => {
            const cs = c / 255;
            return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
        };
        const r = channel(rgb.r);
        const g = channel(rgb.g);
        const b = channel(rgb.b);
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }
}
